// ═══════════════════════════════════════════════════════════════════════════
// ✅ VENC-KPI-001 (2026-08-08) — Panel de inteligencia comercial
// Ubicación: frontend/src/PanelVencimientos.js
// ───────────────────────────────────────────────────────────────────────────
// POR QUÉ EXISTE:
//   El módulo de Vencimientos mostraba tres contadores ("445 vencido, 0 por
//   vencer, 983 vigente") y un acordeón de meses. Eso le sirve a quien va a
//   llamar, pero no a quien dirige: un gerente abre esta pantalla para saber
//   cuánta plata entra este mes y cuánta se está yendo, y esa respuesta no
//   estaba en ningún lado.
//
//   Este panel responde cuatro preguntas, en este orden:
//     1. ¿Cuántos clientes debo atender este mes y cuánto vale eso?
//     2. ¿Cuántos ya volvieron? (tasa de retorno contra el promedio histórico)
//     3. ¿Cuánto se me está yendo hoy? (vencidos sin gestionar, en pesos)
//     4. ¿Qué viene en los próximos meses? (para planear caja e inventario)
//
// SIN DEPENDENCIAS NUEVAS: las gráficas son SVG puro, el mismo patrón de
// CajaGraficas.js. Cero peso extra en el bundle.
//
// Los datos vienen de GET /api/vencimientos/estadisticas — una sola llamada.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const MESES_CORTO = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const fmtPesos = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0,
}).format(n || 0);

// Cifras grandes en formato corto: $12,4 M — en una tarjeta de KPI el peso
// exacto es ruido; lo que importa es el orden de magnitud.
const fmtCorto = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1).replace('.', ',')} MM`;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1).replace('.', ',')} M`;
  if (Math.abs(v) >= 1_000) return `$${Math.round(v / 1_000)} K`;
  return fmtPesos(v);
};

const etiquetaMes = (k) => {
  if (!k) return '';
  const [y, m] = k.split('-');
  return `${MESES_CORTO[Number(m) - 1] || m} ${String(y).slice(2)}`;
};

// ── ✅ VENC-KPI-003 — Navegación por mes ──────────────────────────────────
// Mismo patrón que el panel de Operación del Dashboard: el mes se maneja como
// string 'YYYY-MM' y se mueve en UTC para que un cambio de mes en la frontera
// de medianoche no salte dos meses por la zona horaria.
const MESES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const etiquetaMesLargo = (k) => {
  if (!k) return '';
  const [y, m] = k.split('-');
  const nombre = MESES_LARGO[Number(m) - 1] || m;
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${y}`;
};

const mesActualCO = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Bogota', year: 'numeric', month: '2-digit',
}).format(new Date()).slice(0, 7);

const moverMes = (mes, delta) => {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

// ── Paleta ────────────────────────────────────────────────────────────────
const C = {
  tinta: '#1a1a2e',
  violeta: '#7c3aed',
  violetaSuave: 'rgba(124,58,237,0.10)',
  verde: '#10b981',
  verdeSuave: 'rgba(16,185,129,0.12)',
  rojo: '#dc2626',
  rojoSuave: 'rgba(220,38,38,0.10)',
  ambar: '#f59e0b',
  ambarSuave: 'rgba(245,158,11,0.12)',
  azul: '#3b82f6',
  gris: '#6b7280',
  borde: '#eceaf3',
};

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 },
  gridKpi: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(215px,1fr))', gap: 12 },
  card: {
    background: '#fff', borderRadius: 16, padding: '16px 18px',
    border: `1px solid ${C.borde}`, boxShadow: '0 1px 3px rgba(16,12,40,0.05)',
    position: 'relative', overflow: 'hidden',
  },
  cardTitulo: { fontSize: 11.5, fontWeight: 700, color: C.gris, textTransform: 'uppercase', letterSpacing: 0.4, margin: 0 },
  cardCifra: { fontSize: 30, fontWeight: 800, color: C.tinta, lineHeight: 1.1, margin: '8px 0 2px', letterSpacing: -0.6 },
  cardPie: { fontSize: 12, color: C.gris, margin: 0, lineHeight: 1.45 },
  panel: {
    background: '#fff', borderRadius: 16, padding: '18px 20px',
    border: `1px solid ${C.borde}`, boxShadow: '0 1px 3px rgba(16,12,40,0.05)',
  },
  panelTitulo: { fontSize: 14.5, fontWeight: 800, color: C.tinta, margin: 0 },
  panelSub: { fontSize: 12, color: C.gris, margin: '3px 0 14px', lineHeight: 1.5 },
  fila2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(330px,1fr))', gap: 12 },
  chip: (bg, color) => ({
    display: 'inline-block', background: bg, color, fontSize: 11, fontWeight: 700,
    padding: '3px 9px', borderRadius: 999,
  }),
  // ✅ VENC-KPI-003 — navegador de mes
  nav: {
    display: 'flex', alignItems: 'center', gap: 4, background: '#fff',
    border: `1px solid ${C.borde}`, borderRadius: 10, padding: 4,
  },
  navBtn: {
    border: 'none', background: '#f4f2f9', borderRadius: 8, width: 32, height: 32,
    cursor: 'pointer', fontSize: 18, fontWeight: 700, color: C.violeta, lineHeight: 1,
  },
  navMes: {
    fontWeight: 800, fontSize: 13, color: C.tinta, minWidth: 116, textAlign: 'center',
    padding: '0 4px', whiteSpace: 'nowrap',
  },
  navHoy: {
    background: 'none', border: 'none', padding: 0, color: C.violeta,
    fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline',
  },
  cinta: {
    display: 'flex', gap: 10, alignItems: 'center', background: C.violetaSuave,
    border: `1px solid rgba(124,58,237,0.22)`, borderRadius: 12, padding: '10px 14px',
    fontSize: 12.5, color: '#5b21b6', lineHeight: 1.55, flexWrap: 'wrap',
  },
  aviso: {
    display: 'flex', gap: 10, alignItems: 'flex-start', background: '#fffbeb',
    border: '1px solid #fde68a', borderRadius: 12, padding: '11px 14px',
    fontSize: 12.5, color: '#92400e', lineHeight: 1.55,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Tarjeta de KPI. `acento` pinta la barra lateral de color — es lo que hace
// que el ojo distinga de un vistazo "esto es plata que entra" de "esto es
// plata que se va", sin tener que leer.
// ═══════════════════════════════════════════════════════════════════════════
const Kpi = ({ titulo, cifra, pie, acento = C.violeta, fondo, chip }) => (
  <div style={{ ...S.card, background: fondo || '#fff' }}>
    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: acento }} />
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
      <p style={S.cardTitulo}>{titulo}</p>
      {chip}
    </div>
    <p style={{ ...S.cardCifra, color: acento === C.rojo ? C.rojo : C.tinta }}>{cifra}</p>
    <p style={S.cardPie}>{pie}</p>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// Barras de venta proyectada por mes. La barra del mes en curso va sólida y
// las siguientes translúcidas: proyección no es lo mismo que realidad, y la
// gráfica debe decirlo sin una nota al pie.
// ═══════════════════════════════════════════════════════════════════════════
const GraficaProyeccion = ({ datos, mesActual }) => {
  if (!datos?.length) return <p style={S.cardPie}>Sin vencimientos futuros cargados.</p>;

  const W = 660, H = 210, padX = 46, padY = 26;
  const max = Math.max(...datos.map(d => d.ventaProyectada), 1);
  const anchoUtil = W - padX * 2;
  const paso = anchoUtil / datos.length;
  const anchoBarra = Math.min(paso * 0.55, 64);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} role="img"
         aria-label="Venta proyectada por mes">
      {[0, 0.5, 1].map(f => (
        <line key={f} x1={padX} x2={W - padX / 2} y1={padY + (H - padY * 2) * (1 - f)}
              y2={padY + (H - padY * 2) * (1 - f)} stroke={C.borde} strokeWidth="1" />
      ))}
      {datos.map((d, i) => {
        const alto = (d.ventaProyectada / max) * (H - padY * 2);
        const x = padX + paso * i + (paso - anchoBarra) / 2;
        const y = H - padY - alto;
        const esActual = d.mes === mesActual;
        return (
          <g key={d.mes}>
            <rect x={x} y={y} width={anchoBarra} height={Math.max(alto, 2)} rx="6"
                  fill={esActual ? C.violeta : C.violetaSuave}
                  stroke={esActual ? 'none' : C.violeta} strokeWidth={esActual ? 0 : 1.2} />
            <text x={x + anchoBarra / 2} y={y - 7} textAnchor="middle"
                  fontSize="11" fontWeight="700" fill={C.tinta}>
              {fmtCorto(d.ventaProyectada)}
            </text>
            <text x={x + anchoBarra / 2} y={H - padY + 15} textAnchor="middle"
                  fontSize="11" fontWeight={esActual ? 800 : 600} fill={esActual ? C.violeta : C.gris}>
              {etiquetaMes(d.mes)}
            </text>
            <text x={x + anchoBarra / 2} y={H - padY + 28} textAnchor="middle"
                  fontSize="10" fill={C.gris}>
              {d.clientesEsperados} cli
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Retorno mes a mes: cuántos clientes de los que vencían efectivamente
// volvieron a recargar. Es EL indicador de salud del negocio de recarga —
// una base grande con retorno bajo es una base que se está evaporando.
// ═══════════════════════════════════════════════════════════════════════════
const GraficaRetorno = ({ datos, promedio }) => {
  if (!datos?.length) return <p style={S.cardPie}>Todavía no hay meses cerrados para comparar.</p>;

  const W = 660, H = 220, padX = 42, padTop = 22, padBot = 40;
  const altoUtil = H - padTop - padBot;
  const paso = (W - padX * 2) / Math.max(datos.length - 1, 1);
  const y = (pct) => padTop + altoUtil * (1 - Math.min(pct, 100) / 100);
  const puntos = datos.map((d, i) => ({ x: padX + paso * i, y: y(d.tasaRetorno), d }));
  const linea = puntos.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const area = `${linea} L${puntos[puntos.length - 1].x},${H - padBot} L${puntos[0].x},${H - padBot} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} role="img"
         aria-label="Tasa de retorno de clientes por mes">
      <defs>
        <linearGradient id="gradRetorno" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.verde} stopOpacity="0.28" />
          <stop offset="100%" stopColor={C.verde} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {[0, 25, 50, 75, 100].map(p => (
        <g key={p}>
          <line x1={padX} x2={W - padX / 2} y1={y(p)} y2={y(p)} stroke={C.borde} strokeWidth="1" />
          <text x={padX - 8} y={y(p) + 3.5} textAnchor="end" fontSize="10" fill={C.gris}>{p}%</text>
        </g>
      ))}

      {promedio > 0 && (
        <g>
          <line x1={padX} x2={W - padX / 2} y1={y(promedio)} y2={y(promedio)}
                stroke={C.ambar} strokeWidth="1.5" strokeDasharray="5 4" />
          <text x={W - padX / 2} y={y(promedio) - 6} textAnchor="end" fontSize="10.5"
                fontWeight="700" fill={C.ambar}>
            promedio {promedio}%
          </text>
        </g>
      )}

      <path d={area} fill="url(#gradRetorno)" />
      <path d={linea} fill="none" stroke={C.verde} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

      {puntos.map((p) => (
        <g key={p.d.mes}>
          <circle cx={p.x} cy={p.y} r="4.5" fill="#fff" stroke={C.verde} strokeWidth="2.5" />
          <title>{`${etiquetaMes(p.d.mes)}: ${p.d.clientesRegresaron} de ${p.d.clientesEsperados} clientes regresaron (${p.d.tasaRetorno}%)`}</title>
          <text x={p.x} y={H - padBot + 16} textAnchor="middle" fontSize="10" fill={C.gris}>
            {etiquetaMes(p.d.mes)}
          </text>
          <text x={p.x} y={H - padBot + 29} textAnchor="middle" fontSize="10" fontWeight="700" fill={C.tinta}>
            {p.d.tasaRetorno}%
          </text>
        </g>
      ))}
    </svg>
  );
};

// Barra horizontal simple — para empresas y equipos más recargados.
const BarrasHorizontal = ({ items, etiqueta, valor, sufijo, color = C.violeta }) => {
  if (!items?.length) return <p style={S.cardPie}>Sin datos para este corte.</p>;
  const max = Math.max(...items.map(valor), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: C.tinta, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {etiqueta(it)}
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 800, color, whiteSpace: 'nowrap' }}>
              {sufijo(it)}
            </span>
          </div>
          <div style={{ height: 8, background: '#f4f2f9', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${(valor(it) / max) * 100}%`, height: '100%', background: color, borderRadius: 999 }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ✅ VENC-KPI-002 (2026-08-08) — Balance comparativo de 6 meses
// ───────────────────────────────────────────────────────────────────────────
// Las tarjetas de arriba responden por el mes en curso, que todavía está
// corriendo. Un mes cerrado se juzga distinto: ya no es proyección, es
// resultado. Esta tabla pone los últimos 6 meses cerrados uno al lado del otro
// para ver la tendencia sin navegar entre meses.
//
// La columna que importa es la última: DEJADO DE FACTURAR. Es la venta que
// estaba disponible en ese mes y no se cerró — el costo real de no llamar.
// Un gerente que ve esa columna en rojo entiende el problema sin que nadie se
// lo explique.
// ═══════════════════════════════════════════════════════════════════════════
const TablaComparativa = ({ meses, mesActualKey, enCurso = true }) => {
  if (!meses?.length) return <p style={S.cardPie}>Todavía no hay meses para comparar.</p>;

  const tdBase = { padding: '10px 10px', fontSize: 12.5, borderBottom: `1px solid ${C.borde}`, whiteSpace: 'nowrap' };
  const th = { ...tdBase, fontSize: 11, fontWeight: 700, color: C.gris, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `2px solid ${C.borde}` };

  // Referencia para pintar el retorno: el mejor mes del periodo. Comparar
  // contra el propio historial es más honesto que contra un número inventado.
  const mejorRetorno = Math.max(...meses.map(m => m.tasaRetorno), 1);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>Mes</th>
            <th style={{ ...th, textAlign: 'right' }}>Vencían</th>
            <th style={{ ...th, textAlign: 'right' }}>Volvieron</th>
            <th style={{ ...th, textAlign: 'left', minWidth: 130 }}>Retorno</th>
            <th style={{ ...th, textAlign: 'right' }}>Venta disponible</th>
            <th style={{ ...th, textAlign: 'right' }}>Facturado</th>
            <th style={{ ...th, textAlign: 'right' }}>Dejado de facturar</th>
          </tr>
        </thead>
        <tbody>
          {meses.map(m => {
            const esActual = m.mes === mesActualKey;
            const perdido = Math.max(m.ventaProyectada - m.ventaRealizada, 0);
            return (
              <tr key={m.mes} style={{ background: esActual ? 'rgba(124,58,237,0.045)' : 'transparent' }}>
                <td style={{ ...tdBase, fontWeight: esActual ? 800 : 700, color: esActual ? C.violeta : C.tinta }}>
                  {etiquetaMes(m.mes)}
                  {esActual && (
                    <span style={{ ...S.chip(C.violetaSuave, C.violeta), marginLeft: 7, fontSize: 10 }}>
                      {enCurso ? 'en curso' : 'viendo'}
                    </span>
                  )}
                </td>
                <td style={{ ...tdBase, textAlign: 'right', color: C.tinta }}>{m.clientesEsperados.toLocaleString('es-CO')}</td>
                <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: C.verde }}>{m.clientesRegresaron.toLocaleString('es-CO')}</td>
                <td style={tdBase}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 7, background: '#f4f2f9', borderRadius: 999, overflow: 'hidden', minWidth: 52 }}>
                      <div style={{
                        width: `${(m.tasaRetorno / mejorRetorno) * 100}%`, height: '100%', borderRadius: 999,
                        background: m.tasaRetorno >= mejorRetorno * 0.7 ? C.verde : m.tasaRetorno >= mejorRetorno * 0.35 ? C.ambar : C.rojo,
                      }} />
                    </div>
                    <span style={{ fontWeight: 800, color: C.tinta, minWidth: 42, textAlign: 'right' }}>{m.tasaRetorno}%</span>
                  </div>
                </td>
                <td style={{ ...tdBase, textAlign: 'right', color: C.tinta }}>{fmtCorto(m.ventaProyectada)}</td>
                <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: C.verde }}>{fmtCorto(m.ventaRealizada)}</td>
                <td style={{ ...tdBase, textAlign: 'right', fontWeight: 800, color: perdido > 0 ? C.rojo : C.gris }}>
                  {perdido > 0 ? `−${fmtCorto(perdido)}` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
export default function PanelVencimientos({ recargar = 0, onVerVencidos }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [abierto, setAbierto] = useState(true);
  // ✅ VENC-KPI-003 — mes que se está mirando. Arranca en el mes en curso, así
  // que quien no navega ve exactamente lo mismo que antes.
  const [mes, setMes] = useState(mesActualCO);

  const cargar = useCallback(async (m) => {
    setCargando(true); setError(null);
    try {
      const r = await fetch(`${API}/vencimientos/estadisticas?mes=${m}`, { headers: authHeaders() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'No se pudo cargar el panel');
      setDatos(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(mes); }, [cargar, mes, recargar]);

  // Solo la PRIMERA carga muestra el cartel de espera. Al cambiar de mes se
  // mantiene en pantalla el mes anterior atenuado: si el panel se vaciara en
  // cada clic, navegar seis meses sería seis parpadeos.
  if (cargando && !datos) {
    return (
      <div style={{ ...S.panel, textAlign: 'center', color: C.gris, fontSize: 13, padding: '26px 20px' }}>
        Calculando indicadores del mes…
      </div>
    );
  }
  if (error && !datos) {
    return (
      <div style={{ ...S.panel, borderColor: '#fecaca', background: '#fef2f2', color: '#991b1b', fontSize: 13 }}>
        No se pudieron cargar los indicadores: {error}
      </div>
    );
  }
  if (!datos) return null;

  const { mesActual, retornoPromedio6m, historico, proyeccion, vencidos, empresas, topEquipos, cobertura } = datos;

  const pendientes = Math.max(mesActual.clientesEsperados - mesActual.clientesRegresaron, 0);
  const porCobrar = Math.max(mesActual.ventaProyectada - mesActual.ventaRealizada, 0);
  const diferencia = Math.round((mesActual.tasaRetorno - retornoPromedio6m) * 10) / 10;
  const mejorQuePromedio = diferencia >= 0;

  // ✅ VENC-KPI-003 — el mes consultado manda sobre los textos.
  // Un mes cerrado NO se lee igual que el mes en curso: "falta por facturar"
  // en septiembre es una tarea pendiente; en abril es plata que ya se perdió.
  // Decirlo con las mismas palabras sería mentir por comodidad.
  const enCurso = datos.esMesEnCurso !== false;
  const mesVisto = datos.mesConsultado || mesActual.mes;
  const hoyKey = datos.mesEnCurso || mesActualCO();
  const primerMes = datos.rango?.primerMes || mesVisto;
  const hayAnterior = moverMes(mesVisto, -1) >= primerMes;
  const haySiguiente = mesVisto < hoyKey;

  return (
    <div style={S.wrap}>
      {/* ── Encabezado plegable: un gerente lo quiere ver, una vendedora que
             solo va a llamar prefiere el espacio para la lista. ─────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ ...S.panelTitulo, fontSize: 16 }}>
            📊 {enCurso ? 'Inteligencia del mes' : 'Inteligencia de un mes cerrado'}
          </h3>
          <p style={{ ...S.panelSub, margin: '2px 0 0' }}>
            {etiquetaMesLargo(mesVisto)} · calculado sobre tu base completa y tu lista de precios
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* ✅ VENC-KPI-003 — navegador de meses (mismo patrón del Dashboard) */}
          {abierto && (
            <div style={S.nav}>
              <button
                style={{ ...S.navBtn, opacity: hayAnterior ? 1 : 0.3, cursor: hayAnterior ? 'pointer' : 'default' }}
                disabled={!hayAnterior}
                onClick={() => setMes(moverMes(mesVisto, -1))}
                title="Mes anterior" aria-label="Mes anterior">‹</button>
              <span style={S.navMes}>{etiquetaMesLargo(mesVisto)}</span>
              <button
                style={{ ...S.navBtn, opacity: haySiguiente ? 1 : 0.3, cursor: haySiguiente ? 'pointer' : 'default' }}
                disabled={!haySiguiente}
                onClick={() => setMes(moverMes(mesVisto, 1))}
                title="Mes siguiente" aria-label="Mes siguiente">›</button>
            </div>
          )}
          <button onClick={() => setAbierto(!abierto)} style={{
            background: '#fff', border: `1px solid ${C.borde}`, borderRadius: 9,
            padding: '7px 14px', fontSize: 12.5, fontWeight: 700, color: C.violeta, cursor: 'pointer',
          }}>
            {abierto ? '▲ Ocultar panel' : '▼ Ver panel'}
          </button>
        </div>
      </div>

      {abierto && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, opacity: cargando ? 0.55 : 1, transition: 'opacity .18s' }}>
          {/* Aviso de contexto: sin esto, quien deja el panel en un mes viejo y
              vuelve mañana lee cifras de archivo creyendo que son de hoy. */}
          {!enCurso && (
            <div style={S.cinta}>
              <span style={{ fontSize: 15 }}>🗄️</span>
              <span>
                Estás viendo <strong>{etiquetaMesLargo(mesVisto)}</strong>, un mes ya cerrado. Estas cifras son
                resultado, no proyección: lo que no se facturó en ese mes ya no se recupera ahí.
              </span>
              <button onClick={() => setMes(hoyKey)} style={S.navHoy}>Volver al mes en curso</button>
            </div>
          )}

          {error && (
            <div style={{ ...S.aviso, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>
              <span style={{ fontSize: 15 }}>⚠️</span>
              <span>No se pudo actualizar el panel: {error}. Se mantienen las últimas cifras cargadas.</span>
            </div>
          )}

          {/* ── KPIs ───────────────────────────────────────────────────── */}
          <div style={S.gridKpi}>
            <Kpi
              titulo={enCurso ? 'Clientes esperados este mes' : 'Clientes que vencían'}
              cifra={mesActual.clientesEsperados.toLocaleString('es-CO')}
              acento={C.violeta}
              pie={enCurso
                ? <>{mesActual.cantidadEquipos.toLocaleString('es-CO')} equipos por recargar · <strong>{pendientes}</strong> sin atender aún</>
                : <>{mesActual.cantidadEquipos.toLocaleString('es-CO')} equipos · <strong>{pendientes}</strong> nunca volvieron</>}
            />
            <Kpi
              titulo={enCurso ? 'Venta esperada del mes' : 'Venta que estaba disponible'}
              cifra={fmtCorto(mesActual.ventaProyectada)}
              acento={enCurso ? C.verde : (porCobrar > 0 ? C.ambar : C.verde)}
              fondo={enCurso ? 'linear-gradient(180deg,#ffffff,#f6fffb)' : '#fff'}
              pie={enCurso
                ? <>Ya facturado <strong>{fmtCorto(mesActual.ventaRealizada)}</strong> · falta {fmtCorto(porCobrar)}</>
                : <>Facturado <strong>{fmtCorto(mesActual.ventaRealizada)}</strong> · quedó sin facturar {fmtCorto(porCobrar)}</>}
            />
            <Kpi
              titulo="Tasa de retorno"
              cifra={`${mesActual.tasaRetorno}%`}
              acento={mejorQuePromedio ? C.verde : C.ambar}
              chip={<span style={S.chip(mejorQuePromedio ? C.verdeSuave : C.ambarSuave, mejorQuePromedio ? '#047857' : '#b45309')}>
                {mejorQuePromedio ? '▲' : '▼'} {Math.abs(diferencia)} pts
              </span>}
              pie={<>{mesActual.clientesRegresaron} de {mesActual.clientesEsperados} {enCurso ? 'ya volvieron' : 'volvieron'} · promedio de los 6 meses previos: {retornoPromedio6m}%</>}
            />
            {/* Esta tarjeta NO se mueve con el mes: es la plata que se está
                yendo HOY. Mostrarla como si fuera del mes consultado sería el
                error más caro del panel. */}
            <Kpi
              titulo="Vencidos sin atender (a hoy)"
              cifra={fmtCorto(vencidos.valor)}
              acento={C.rojo}
              fondo="linear-gradient(180deg,#ffffff,#fff7f7)"
              pie={<>
                {vencidos.clientes.toLocaleString('es-CO')} clientes · {vencidos.equipos.toLocaleString('es-CO')} equipos
                {onVerVencidos && (
                  <>
                    {' '}·{' '}
                    <button onClick={onVerVencidos} style={{
                      background: 'none', border: 'none', padding: 0, color: C.rojo,
                      fontWeight: 800, fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
                    }}>ver lista</button>
                  </>
                )}
              </>}
            />
          </div>

          {/* ── Aviso de cobertura de precios ──────────────────────────────
                 Sin esto, un gerente podría leer la proyección como completa
                 cuando le falta valorizar parte de la base. ─────────────── */}
          {mesActual.equiposSinPrecio > 0 && (
            <div style={S.aviso}>
              <span style={{ fontSize: 15 }}>⚠️</span>
              <span>
                <strong>{mesActual.equiposSinPrecio} equipos de {etiquetaMesLargo(mesVisto)} no tienen precio</strong> porque su
                descripción no coincide con ningún producto de tu lista. No se estimaron con un promedio:
                la venta esperada real es <em>mayor</em> a la que ves. Crea esos productos en{' '}
                <strong>Productos</strong> o corrige la descripción del equipo para que la proyección quede completa.
              </span>
            </div>
          )}

          {/* ── Gráficas ──────────────────────────────────────────────────── */}
          <div style={S.panel}>
            <h4 style={S.panelTitulo}>
              Venta esperada — 6 meses desde {etiquetaMesLargo(mesVisto)}
            </h4>
            <p style={S.panelSub}>
              Cada barra suma los equipos que vencen ese mes valorizados con el precio al público de tu
              lista de productos. Es la caja que deberías facturar si todos los clientes regresan.
              {!enCurso && ' Desde un mes cerrado, la primera barra ya es historia, no pronóstico.'}
            </p>
            <GraficaProyeccion datos={proyeccion} mesActual={mesVisto} />
          </div>

          {/* ✅ VENC-KPI-002 — Balance de los últimos 6 meses cerrados + el
              mes en curso, para juzgar la tendencia sin navegar mes a mes. */}
          <div style={S.panel}>
            <h4 style={S.panelTitulo}>
              Balance de los 6 meses previos a {etiquetaMesLargo(mesVisto)}
            </h4>
            <p style={S.panelSub}>
              Meses ya cerrados: no es proyección, es resultado. La última columna es la venta que estaba
              disponible y no se cerró — el costo real de no alcanzar a llamar.
            </p>
            <TablaComparativa
              meses={[...historico.slice(-6), mesActual]}
              mesActualKey={mesVisto}
              enCurso={enCurso}
            />
            {(() => {
              const cerrados = historico.slice(-6);
              const perdidoTotal = cerrados.reduce((s, m) => s + Math.max(m.ventaProyectada - m.ventaRealizada, 0), 0);
              if (!cerrados.length || perdidoTotal <= 0) return null;
              return (
                <div style={{ ...S.aviso, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginTop: 14 }}>
                  <span style={{ fontSize: 15 }}>💸</span>
                  <span>
                    En los {cerrados.length} meses previos a {etiquetaMesLargo(mesVisto)} quedaron <strong>{fmtCorto(perdidoTotal)}</strong> sin
                    facturar de clientes que ya tenías y a los que solo había que llamar. Recuperar aunque sea
                    una parte cuesta menos que conseguir clientes nuevos por el mismo valor.
                  </span>
                </div>
              );
            })()}
          </div>

          <div style={S.panel}>
            <h4 style={S.panelTitulo}>¿Cuántos clientes están regresando?</h4>
            <p style={S.panelSub}>
              De los clientes que vencían cada mes, qué porcentaje volvió a recargar. La línea punteada es tu
              promedio de los 6 meses previos a {etiquetaMesLargo(mesVisto)}: por debajo de ella, el mes va
              perdiendo clientes.
            </p>
            <GraficaRetorno datos={historico} promedio={retornoPromedio6m} />
          </div>

          <div style={S.fila2}>
            <div style={S.panel}>
              <h4 style={S.panelTitulo}>Por empresa que factura</h4>
              <p style={S.panelSub}>
                Desde {etiquetaMesLargo(mesVisto)} en adelante — sirve para proyectar por razón social.
              </p>
              <BarrasHorizontal
                items={empresas.slice(0, 6)}
                etiqueta={(e) => e.empresa}
                valor={(e) => e.venta}
                sufijo={(e) => `${fmtCorto(e.venta)} · ${e.clientes} cli`}
              />
            </div>

            <div style={S.panel}>
              <h4 style={S.panelTitulo}>
                {enCurso ? 'Qué se recarga más este mes' : `Qué se recargaba en ${etiquetaMesLargo(mesVisto)}`}
              </h4>
              <p style={S.panelSub}>
                {enCurso
                  ? 'Para tener el inventario listo antes de que empiecen a llamar.'
                  : 'Sirve para comparar la mezcla de equipos contra la del mes actual.'}
              </p>
              <BarrasHorizontal
                items={topEquipos}
                etiqueta={(e) => e.equipo}
                valor={(e) => e.cantidad}
                sufijo={(e) => `${e.cantidad} und`}
                color={C.azul}
              />
            </div>
          </div>

          <p style={{ fontSize: 11.5, color: C.gris, margin: 0, lineHeight: 1.6 }}>
            Precios tomados de tu lista de productos ({cobertura.productosConPrecio} productos con precio de venta).
            Un cliente cuenta como “regresó” cuando su ciclo quedó cerrado con venta: orden asociada,
            marcado como gestionado o ciclo renovado.
          </p>
        </div>
      )}
    </div>
  );
}
